import { useMemo, useRef, useState } from 'react'
import { useClickAway, useMedia } from 'react-use'
import styled from 'styled-components'
import { formatDate, parseDate } from '~/prix/types/date'
import ViewSelectModalVariable from '~/packages/sentinel/viewSelectModalVariable.component'
import ViewSelectModalTime from '~/packages/sentinel/viewSelectModalTime.component'
import ViewSelectModalSpace from '~/packages/sentinel/viewSelectModalSpace.component'
import { ReportWithLayer } from '~/packages/sentinel/reports'
import { Layer, TimeIncidence } from '~/packages/variable/spaceTime.types'
import {
  VariableAndOptionalResult,
  VariableWithConclusion,
} from '~/packages/variable/variables.typing'
import { BaseButton } from './Button'
import { attributionMarginLeftPx } from '../map/map'
import { ToolsAndOptionalResult } from '~/packages/tools/tools.typing'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import CloseIcon from '~/components/icons/ui/64px_small-remove.svg'
import { extractAppliedFilters } from '~/packages/sentinel/viewSelectTopBoxDesktop.component'

const Wrapper = styled.aside`
  position: absolute;
  top: ${({ theme }) => theme.padding(0.6)};
  left: ${({ theme }) => theme.padding(0.6)};
  z-index: 4;
  display: flex;
  flex-direction: column;
  width: min(600px, calc(100% - ${({ theme }) => theme.padding(1.2)}));
  max-width: 600px;

  .controls {
    display: flex;
    width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      gap: 7px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    top: 56px;
    width: auto;
    right: ${({ theme }) => theme.padding(0.6)};
    min-height: auto;
  }

  .controls button {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
    flex-direction: column;
    border: none;
    border-radius: 0;
    padding: 1rem;
    background-color: white;
    box-shadow: ${({ theme }) => theme.shadow(1)};
    color: #363636;
    cursor: pointer;
    text-align: left;

    @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
      background-color: ${({ theme }) => theme.backdropBackground};
      -webkit-backdrop-filter: ${({ theme }) => theme.backdropFilter};
      backdrop-filter: ${({ theme }) => theme.backdropFilter};
    }

    &:hover {
      background-color: #fff;
      box-shadow: ${({ theme }) => theme.shadow(2)};
    }

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
      border-right: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
      border-left: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      background: white;
      border: none !important;
      border-radius: 8px;
      padding: 0.75rem;
      gap: 4px;
      box-shadow: ${({ theme }) => theme.shadow(1)};
      line-height: 0.8rem;

      h2 {
        font-size: 0.65rem;
        margin: 0;
      }

      span {
        font-size: 0.9rem;
      }

      small {
        font-size: 0.8rem;
        color: #606060;
        display: none;
      }
    }
  }

  h2 {
    margin: 0 0 ${({ theme }) => theme.padding(0.3)};
    color: #363636;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  span,
  small {
    color: #363636;
  }

  small {
    margin-top: ${({ theme }) => theme.padding(0.2)};
  }
`

const WrapperBottomButtons = styled.div`
  position: absolute;
  top: 0px;
  right: 0.6rem;
  bottom: calc(${attributionMarginLeftPx}px + 0.3rem);
  left: 0.6rem;
  z-index: 4;

  display: flex;
  justify-content: space-between;
  align-items: end;

  gap: 0.3rem;
  padding-bottom: 0.3rem;
  pointer-events: none;

  > * {
    pointer-events: all;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    justify-content: end;
    align-items: start;
  }
`

const MobileMoreInfoWrapper = styled.div<{ $isOpen: boolean }>`
  position: relative;

  .more-info-button {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: none;
    height: 40px;
    width: 40px;
    background-color: white;
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow(1)};
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
  }

  .more-info-content {
    position: absolute;
    bottom: calc(40px + ${({ theme }) => theme.padding(0.3)});
    left: 0;
    width: min(300px, calc(100vw - ${({ theme }) => theme.padding(1.2)}));
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
    transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '30%')});
    transition:
      opacity 0.3s ease-in-out,
      transform 0.3s ease-in-out,
      visibility 0.3s ease-in-out;
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'all' : 'none')};
  }
`

interface BaseControlsProps {
  variables: VariableWithConclusion[]
  layer: Layer
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  stateId: number | null
  stateName: string | null
  onChangeReport?: (report: ReportWithLayer | null) => void
  isDisabled?: boolean
  moreInfo?: React.ReactNode
}

function getVariableSubtitle(layer: Layer) {
  const hasLabel = Boolean(layer.variable.label)
  const hasTitle = Boolean(layer.variable.title)

  if (hasLabel && hasTitle && layer.variable.title !== layer.variable.label) {
    return layer.variable.title
  }

  return null
}

export const BaseToolControls = ({
  variables,
  layer,
  onChangeLayer,
  stateId,
  stateName,
  onChangeReport = () => undefined,
  isDisabled = false,
  moreInfo,
}: BaseControlsProps) => {
  const [isVariableModalOpen, setIsVariableModalOpen] = useState(false)
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false)
  const [isSpaceModalOpen, setIsSpaceModalOpen] = useState(false)
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false)
  const moreInfoRef = useRef<HTMLDivElement | null>(null)
  const isMobile = useMedia('(max-width: 768px)')

  useClickAway(moreInfoRef, () => {
    if (isMobile && isMoreInfoOpen) {
      setIsMoreInfoOpen(false)
    }
  })

  const currentTime = layer.time as TimeIncidence
  const variableSubtitle = useMemo(() => getVariableSubtitle(layer), [layer])

  const periodLabel = useMemo(() => {
    if (!currentTime.startDate || !currentTime.endDate) {
      return null
    }

    return `${formatDate(parseDate(currentTime.startDate))} - ${formatDate(parseDate(currentTime.endDate))}`
  }, [currentTime.endDate, currentTime.startDate])

  const appliedFilters = useMemo(() => {
    return extractAppliedFilters(
      (layer?.variable as VariableAndOptionalResult)?.filter,
      (layer?.variable as VariableAndOptionalResult)?.filterableBy,
    )
  }, [
    (layer?.variable as VariableAndOptionalResult)?.filter,
    (layer?.variable as VariableAndOptionalResult)?.filterableBy,
    (layer?.variable as VariableAndOptionalResult)?.key,
    (layer?.variable as VariableAndOptionalResult)?.presetKey,
  ])

  const restrictColumn = (layer.variable as ToolsAndOptionalResult)?.restrictView

  return (
    <>
      <Wrapper>
        <div className='controls'>
          {!restrictColumn?.includes('variable') && (
            <button
              type='button'
              disabled={isDisabled}
              onClick={() => {
                setIsVariableModalOpen(true)
              }}
            >
              <h2>O quê</h2>
              <span>{layer.variable.label || layer.variable.title}</span>
              {appliedFilters.length > 0 ? (
                <small>Filtros: {appliedFilters.join(', ')}</small>
              ) : (
                variableSubtitle && <small>{variableSubtitle}</small>
              )}
            </button>
          )}
          {!restrictColumn?.includes('time') && (
            <button
              type='button'
              disabled={isDisabled}
              onClick={() => {
                setIsTimeModalOpen(true)
              }}
            >
              <h2>Quando</h2>
              <span>{currentTime.title}</span>
              {periodLabel && <small>{periodLabel}</small>}
            </button>
          )}
          {!restrictColumn?.includes('space') && (
            <button
              type='button'
              disabled={isDisabled}
              onClick={() => {
                setIsSpaceModalOpen(true)
              }}
            >
              <h2>Onde</h2>
              <span>{layer.space.delimitationTitle}</span>
              <small>Delimitação geográfica</small>
            </button>
          )}
        </div>
      </Wrapper>
      {!restrictColumn?.includes('variable') && (
        <ViewSelectModalVariable
          variables={variables}
          isOpen={isVariableModalOpen}
          onClose={() => setIsVariableModalOpen(false)}
          layer={layer}
          baseLayer={layer}
          onChangeLayer={onChangeLayer}
          stateId={stateId}
          stateName={stateName}
        />
      )}
      {!restrictColumn?.includes('time') && (
        <ViewSelectModalTime
          layer={layer}
          isOpen={isTimeModalOpen}
          onClose={() => setIsTimeModalOpen(false)}
          onChangeLayer={onChangeLayer}
          onChangeReport={onChangeReport}
          showPreviewData={false}
          showAnalyseTimeButton={false}
        />
      )}
      {!restrictColumn?.includes('space') && (
        <ViewSelectModalSpace
          layer={layer}
          isOpen={isSpaceModalOpen}
          onClose={() => setIsSpaceModalOpen(false)}
          onChangeLayer={onChangeLayer}
          delimitationOnly
        />
      )}
      {moreInfo && (
        <WrapperBottomButtons>
          {isMobile ? (
            <MobileMoreInfoWrapper ref={moreInfoRef} $isOpen={isMoreInfoOpen}>
              <button
                type='button'
                className='more-info-button'
                aria-label='Mais informações'
                onClick={() => setIsMoreInfoOpen(previousValue => !previousValue)}
              >
                {isMoreInfoOpen ? (
                  <CloseIcon fill={'#363636'} width={32} height={32} />
                ) : (
                  <FontAwesomeIcon icon={faInfoCircle} fontSize={18} color='#363636' />
                )}
              </button>
              <div className='more-info-content'>{moreInfo}</div>
            </MobileMoreInfoWrapper>
          ) : (
            moreInfo
          )}
        </WrapperBottomButtons>
      )}
    </>
  )
}
